<script setup lang="ts">
import { store as vuex } from '@/store/store';
import { computed, onMounted, shallowRef } from 'vue';
import { UptimeOutage, UptimeService, normalizeUptimeService } from '@/utils/api/uptime';
import {
  getOutageDuration,
  getUptimeStatusColor,
  getUptimeStatusIcon,
  getUptimeStatusText,
} from '@/modules/uptime/helpers/uptime';
import { eachDayOfInterval, getDayOfYear, isBefore, subDays } from 'date-fns';
import { formatAnyDate } from '@/utils/helpers/dates';
import i18n from '@/localisation/i18n';
import UptimeServiceDialog from '@/modules/uptime/components/UptimeServiceDialog.vue';
import { useLoggerService } from '@/modules/common/services/logger.service';
import axios, { AxiosError } from 'axios';
import { ApiError } from '@/utils/errors/api-error';
import { i18nServerMessage } from '@/utils/helpers/rest-response';

const loggerService = useLoggerService();

const uptimeServices = shallowRef<UptimeService[]>([]);

// An admin has the 'broker-admin', 'broker-user' or 'trader-admin' or 'config-admin' role;
// broker roles share one endpoint, trader/config roles share the other.
async function fetchAdminUptimeServices(): Promise<void> {
  const adminRole =
    vuex.state.loginState.user!.roles.includes('trader-admin') ||
    vuex.state.loginState.user!.roles.includes('config-admin')
      ? 'trader-admin'
      : 'broker-user';

  try {
    const { data } = await axios.get(`/api/1/${adminRole}/uptime/services`);
    data.uptimeServices.forEach((us: UptimeService) => normalizeUptimeService(us));
    uptimeServices.value = data.uptimeServices;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

const showError = shallowRef(false);
const apiErrors = shallowRef<string[]>([]);
const isLoading = shallowRef(true);
const showTooltip = shallowRef(false);
const tooltipTitle = shallowRef('');
const tooltipCaption = shallowRef('');
const tooltipLines = shallowRef<UptimeOutage[] | null>(null);
const uptimeService = shallowRef<UptimeService | null>(null);

const uptimeCache = computed(() => {
  const now = new Date();
  const services = new Map<string, UptimeService>();
  const outages = new Map<string, UptimeOutage[]>();

  // split outages per service into a list per day
  uptimeServices.value.forEach((service) => {
    services.set(service.id, service);
    service.outages.forEach((outage) => {
      eachDayOfInterval({ start: outage.from, end: outage.until }).forEach((dt) => {
        const outageKey = `${service.id}:${getDayOfYear(now) - getDayOfYear(dt)}`;
        const outageList = outages.get(outageKey) || [];
        outageList.push(outage);
        outages.set(outageKey, outageList);
      });
    });
  });

  return { services: services, outages };
});

function getStatusColor(status: string): string {
  return getUptimeStatusColor(status);
}

function getStatusIcon(status: string): string {
  return getUptimeStatusIcon(status);
}

function getServiceOutageClass(serviceId: string, dayNumber: number): string {
  const service = uptimeCache.value.services.get(serviceId)!;
  const date = subDays(new Date(), dayNumber);
  if (isBefore(date, service.rangeStart)) {
    return ''; // block remains `grey` for days the service did not exist yet
  }

  const outageKey = `${serviceId}:${dayNumber}`;
  const outageList = uptimeCache.value.outages.get(outageKey) || [];
  const majorOutageCount = outageList.reduce((count, o) => {
    return o.status === 'MAJOR_OUTAGE' ? count + 1 : count;
  }, 0);

  if (majorOutageCount > 0) {
    return 'error';
  }

  // any outage should be visible by its color
  return outageList.length > 0 ? 'warning' : 'success';
}

function getServiceOutageDate(dayNumber: number): string {
  return formatAnyDate(subDays(new Date(), dayNumber), 'MM/dd');
}

function showOutageDetails(serviceId: string, dayNumber: number): void {
  showTooltip.value = false;
  tooltipTitle.value = '';
  tooltipCaption.value = '';

  const outageKey = `${serviceId}:${dayNumber}`;
  const outageList = uptimeCache.value.outages.get(outageKey);

  if (!outageList) {
    return; // 100% uptime, not need to popup outages
  }

  const service = uptimeCache.value.services.get(serviceId)!; // every service is in the map!
  tooltipTitle.value = service.name;
  tooltipCaption.value = getServiceOutageDate(dayNumber);
  tooltipLines.value = outageList;
  showTooltip.value = true;
}

function getOutageText(o: UptimeOutage): string {
  return i18n.t('uptime.outagePeriod', {
    status: getUptimeStatusText(o.status),
    from: formatAnyDate(o.from, 'HH:mm'),
    duration: getOutageDuration(o),
  }) as string;
}

onMounted(async () => {
  isLoading.value = true;
  showError.value = false;
  try {
    await fetchAdminUptimeServices();
  } catch (err) {
    apiErrors.value = [`${err}`];
    showError.value = true;
    loggerService.warn(err);
  } finally {
    isLoading.value = false;
  }
});
</script>

<!-- eslint-disable vue/no-v-for-template-key-on-child -->
<template>
  <VCard class="flex d-flex flex-column">
    <UptimeServiceDialog
      v-if="uptimeService"
      :service="uptimeService"
      @close-modal="uptimeService = null"
    />

    <VCardTitle class="mb-4">
      <span class="headline">{{ $t('adminUptimeStatistics.title') }}</span>
    </VCardTitle>
    <VCardText v-if="showError">
      <div
        class="v-alert v-alert--dense text--primary text-body-2 text-center"
        :class="{ error: showError }"
      >
        <div>
          {{ apiErrors.join('\n') }}
        </div>
      </div>
    </VCardText>
    <VCardText v-if="isLoading" class="text--primary">
      <VContainer class="flex-column justify-center fill-height">
        <VProgressLinear color="primary" indeterminate></VProgressLinear>
        <h3 class="mt-4">Loading Services ...</h3>
      </VContainer>
    </VCardText>
    <VCardText v-else class="text--primary">
      <VContainer v-if="uptimeServices.length === 0" class="flex-column justify-center fill-height">
        <VAlert icon="mdi-alert" outlined type="warning"
          >Uptime Statistics currently not available
        </VAlert>
      </VContainer>
      <VContainer v-else fluid>
        <VRow v-for="service in uptimeServices" :key="service.name" class="pa-4">
          <VCol>
            <VCard :key="service.name" class="mt-1 mx-auto" elevation="10">
              <VCardText class="pt-0">
                <VRow no-gutters>
                  <VCol col="3">
                    <div class="text-h6 font-weight-light mb-2">{{ service.name }}</div>
                    <div class="subheading font-weight-light grey--text">
                      Status: {{ $t(`uptime.status.${service.status}`) }}
                    </div>
                    <VCol>
                      <VBtn color="secondary" small @click="uptimeService = service">
                        historical uptime
                      </VBtn>
                    </VCol>
                  </VCol>
                  <VCol class="mt-5 scroll-horizontally" cols="9">
                    <VRow no-gutters>
                      <VCol cols="auto">
                        <VMenu bottom>
                          <template #activator="{ on, attrs }">
                            <svg
                              class="uptime-graph"
                              height="52"
                              width="1100"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <template v-for="(ix, i) in 90">
                                <rect
                                  :key="ix"
                                  :class="getServiceOutageClass(service.id, i)"
                                  height="34"
                                  rx="2"
                                  ry="2"
                                  width="10"
                                  :x="i * 12 + 10"
                                  :y="0"
                                  @click="showOutageDetails(service.id, i)"
                                ></rect>
                                <rect
                                  v-if="i % 10 === 0"
                                  :key="'line' + ix"
                                  class="uptime-line"
                                  height="5"
                                  rx="2"
                                  ry="2"
                                  width="1"
                                  :x="i * 12 + 14"
                                  :y="34"
                                ></rect>
                                <text
                                  v-if="i % 10 === 0"
                                  :key="'date' + ix"
                                  class="uptime-label"
                                  :x="i * 12"
                                  :y="52"
                                >
                                  {{ getServiceOutageDate(i) }}
                                </text>
                              </template>
                            </svg>
                          </template>
                          <VContainer v-if="showTooltip" class="mt-16" fluid>
                            <VCard light max-width="500">
                              <VCardTitle>{{ tooltipTitle }}</VCardTitle>
                              <VCardSubtitle>{{ tooltipCaption }}</VCardSubtitle>
                              <VCardText>
                                <div v-for="(o, i) in tooltipLines" :key="i">
                                  <VIcon :color="getStatusColor(o.status)" left small
                                    >{{ getStatusIcon(o.status) }}
                                  </VIcon>
                                  {{ getOutageText(o) }}
                                </div>
                              </VCardText>
                            </VCard>
                          </VContainer>
                        </VMenu>
                      </VCol>
                    </VRow>
                    <VRow no-gutters>
                      <VCol cols="auto">Today</VCol>
                      <VSpacer>
                        <VDivider class="ma-3"></VDivider>
                      </VSpacer>
                      <VCol align-self="end" cols="auto">90 days ago</VCol>
                    </VRow>
                  </VCol>
                </VRow>
              </VCardText>
              <VCardText>
                <VDivider class="my-2"></VDivider>
                <VIcon class="mr-2" :color="getStatusColor(service.status)" small>
                  mdi-clock
                </VIcon>
                <span class="text-caption grey--text font-weight-light">{{
                  $tc('uptime.daysUp', service.days, {
                    percentage: service.percentage.eq(100.0)
                      ? service.percentage.toFixed(0)
                      : service.percentage.toFixed(3),
                    dayCount: service.days,
                  })
                }}</span>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </VCardText>
  </VCard>
</template>

<style lang="scss" scoped>
.uptime-label {
  fill: #777777;
  font-size: 12px;
}

.scroll-horizontally {
  overflow-x: scroll;
}

.uptime-graph {
  cursor: default;

  rect {
    fill: #777777;
  }

  .success {
    fill: green;
  }

  .warning {
    fill: #10bb10;
    cursor: pointer;
  }

  .error {
    fill: red;
    cursor: pointer;
  }
}
</style>
